import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';

/**
 * Clean and format phone number to match backend expectations
 * Note: For better international support, consider using extractLast10Digits() for matching
 */
export function cleanPhoneNumber(phone: string): string {
  if (!phone) return '';
  
  // Remove all non-digit characters except the leading +
  const cleanNumber = phone.replace(/[^\d+]/g, '');
  
  // If it doesn't start with +, assume US/Canada and add +1
  if (!cleanNumber.startsWith('+')) {
    const digitsOnly = cleanNumber.replace(/\D/g, '');
    return digitsOnly ? `+1${digitsOnly}` : '';
  }
  
  return cleanNumber;
}

/**
 * Extract last 10 digits from phone number for matching
 * This is useful for matching phone numbers across different country codes and formats
 */
export function extractLast10Digits(phone: string): string {
  if (!phone) return '';
  
  const digitsOnly = phone.replace(/\D/g, ''); // Remove all non-digits
  return digitsOnly.slice(-10); // Get last 10 digits
}

/**
 * Check if two phone numbers match by comparing their last 10 digits
 */
export function phoneNumbersMatch(phone1: string, phone2: string): boolean {
  if (!phone1 || !phone2) return false;
  
  const last10Digits1 = extractLast10Digits(phone1);
  const last10Digits2 = extractLast10Digits(phone2);
  
  // Both numbers must have at least 10 digits to use this matching
  if (last10Digits1.length >= 10 && last10Digits2.length >= 10) {
    return last10Digits1 === last10Digits2;
  }
  
  // Fall back to exact matching for shorter numbers
  return phone1 === phone2;
}

/**
 * Generate phone number variants for database lookup
 * Updated to use last 10 digits approach for better international support
 */
export function generatePhoneVariants(phone: string): string[] {
  if (!phone) return [];
  
  const digitsOnly = phone.replace(/\D/g, '');
  const last10Digits = digitsOnly.slice(-10);
  
  // If we have 10 or more digits, prioritize the last 10 digits approach
  if (last10Digits.length >= 10) {
    return [
      phone, // Original format
      digitsOnly, // All digits
      last10Digits, // Last 10 digits only
      `+1${last10Digits}`, // US format with last 10 digits
    ];
  }
  
  // For shorter numbers, use the original approach as fallback
  const variants = [
    phone,
    phone.replace(/^\+1/, ''), // Remove +1 prefix
    phone.replace(/\D/g, ''), // Remove all non-digits
    '+1' + phone.replace(/\D/g, '') // Add +1 prefix
  ];
  
  // Remove duplicates and empty strings
  return [...new Set(variants)].filter(v => v.length > 0);
}

/**
 * Validate phone number using libphonenumber-js
 */
export function isValidPhone(phone: string): boolean {
  if (!phone) return false;
  
  try {
    return isValidPhoneNumber(phone);
  } catch (error) {
    return false;
  }
}

/**
 * Format phone number for display
 */
export function formatPhoneForDisplay(phone: string): string {
  if (!phone) return '';
  
  try {
    const phoneNumber = parsePhoneNumber(phone);
    if (phoneNumber) {
      return phoneNumber.formatInternational();
    }
  } catch (error) {
    // If parsing fails, return the original
  }
  
  return phone;
}

/**
 * Normalize phone number for storage (ensures it starts with + and country code)
 */
export function normalizePhoneNumber(phone: string, defaultCountry: string = 'US'): string {
  if (!phone) return '';
  
  try {
    const phoneNumber = parsePhoneNumber(phone, defaultCountry as any);
    if (phoneNumber) {
      return phoneNumber.number;
    }
  } catch (error) {
    // If parsing fails, fall back to basic cleaning
  }
  
  return cleanPhoneNumber(phone);
} 